import { z } from "zod";
import { authorityLevelEnum } from "./enums.js";
import { projectExportConfigSchema } from "./export-registry.js";

// ── Software Boundary ────────────────────────────────────────────

export const softwareRoleEnum = z.enum([
  "enforces",
  "records",
  "facilitates",
  "outside",
]);
export type SoftwareRole = z.infer<typeof softwareRoleEnum>;

export const boundaryConfidenceEnum = z.enum(["confirmed", "hypothesis"]);
export type BoundaryConfidence = z.infer<typeof boundaryConfidenceEnum>;

export const softwareBoundarySchema = z.object({
  domainRule: z.string().min(1),
  softwareRole: softwareRoleEnum,
  explanation: z.string(),
  sourceRef: z.string().optional(),
  confidence: boundaryConfidenceEnum,
});
export type SoftwareBoundary = z.infer<typeof softwareBoundarySchema>;

// ── Governance Reference ─────────────────────────────────────────

export const governanceRefSchema = z.object({
  name: z.string().min(1),
  authorityLevel: authorityLevelEnum,
  sourceId: z.string().uuid().optional(),
});
export type GovernanceRef = z.infer<typeof governanceRefSchema>;

// ── Actor ────────────────────────────────────────────────────────

export const actorSchema = z.object({
  name: z.string().min(1),
  role: z.string().min(1),
  softwareBoundary: z.string().optional(),
});
export type Actor = z.infer<typeof actorSchema>;

// ── Project Context ──────────────────────────────────────────────

export const projectContextSchema = z.object({
  domain: z.string().optional(),
  industry: z.string().optional(),
  governedBy: z.array(governanceRefSchema).optional(),
  actors: z.array(actorSchema).optional(),
  boundaries: z.array(softwareBoundarySchema).optional(),
  architecture: z.string().optional(),
  notes: z.string().optional(),
  discoverySuggestions: z.array(z.string()).optional(),
  lastUpdatedBy: z.enum(["bootstrap", "user", "system"]).optional(),
  lastUpdatedAt: z.string().optional(),
  exportConfig: projectExportConfigSchema.optional(),
});
export type ProjectContext = z.infer<typeof projectContextSchema>;

/** Partial schema for update mutations — all top-level fields optional */
export const projectContextUpdateSchema = projectContextSchema.partial();
export type ProjectContextUpdate = z.infer<typeof projectContextUpdateSchema>;
